import { useState } from "react";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import { useOrderDocumentDownload } from "../../../hooks/api/v2/orderDocument/useOrderDocumentDownload";
import { useOrderDocumentList } from "../../../hooks/api/v2/orderDocument/useOrderDocumentList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSellarUser } from "../../../hooks";
import { downloadPDF } from "../../../utils/downloadHelpers";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Alert } from "../../../components/alert/Alert";
import { SpinnerLegacy } from "../../../components";
import {
  Button,
  H5,
  RadioSelector,
  RadioSelectorOption,
} from "@sellar-io/components";
import dayjs from "dayjs";
import { useOrderDocumentSend } from "../../../hooks/api/v2/orderDocument/useOrderDocumentSend";
import { useToast } from "../../../components/toast/useToast.shared";
import { Toast } from "../../../components/toast/Toast";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { sanitizeEmailArray } from "@sellar-io/common";

type SellarCreditOrderCreateInvoiceDialogProps = {
  orderId: number;
  open: boolean;
  onDismiss: () => void;
};

export const SellarCreditOrderCreateInvoiceDialog = ({
  orderId,
  open,
  onDismiss,
}: SellarCreditOrderCreateInvoiceDialogProps) => {
  const { business } = useSellarCurrentBusiness();
  const { toast } = useToast();
  const orderQuery = useOrder(orderId, {
    enabled: Boolean(orderId),
  });
  const order = orderQuery.data;
  const { user } = useSellarUser();
  const retailerInvoiceEmails: string[] = order?.retailerInvoiceEmails ?? [];
  const buyerEmails =
    retailerInvoiceEmails.length > 0
      ? retailerInvoiceEmails
      : sanitizeEmailArray(order?.Retailer.Business.invoiceEmails ?? []);
  const [errorMessage, setErrorMessage] = useState("");
  const isSupplier = !!business?.Supplier;
  const isBuyer = !!business?.Retailer;
  const orderDocumentQuery = useOrderDocumentList({
    limit: 50,
    query: [
      isSupplier && {
        type: "SupplierId",
        modifier: "EQUALS",
        value: business.Supplier?.id,
      },
      isBuyer && {
        type: "RetailerId",
        modifier: "EQUALS",
        value: business.Retailer?.id,
      },
      { type: "OrderId", modifier: "EQUALS", value: orderId },
    ].filter(Boolean),
  });
  const orderDocumentDownloadMutation = useOrderDocumentDownload();
  const orderDocumentSendMutation = useOrderDocumentSend();
  const sellarCreditInvoice = (orderDocumentQuery.data?.pages[0] ?? []).find(
    (document) => document.type === "invoice",
  );

  const handleDownloadInvoice = ({
    invoice,
  }: {
    invoice: typeof sellarCreditInvoice;
  }) => {
    setErrorMessage("");
    if (!invoice) {
      setErrorMessage("Cannot find invoice associated with this order");
      return;
    }
    orderDocumentDownloadMutation.mutate(
      {
        id: invoice.id,
        type: "invoice",
      },
      {
        onSuccess: (data) => {
          const documentFileName = `Invoice - ${invoice.customId} - ${dayjs(
            invoice.issuedAt ?? new Date(),
          ).format("DD-MM-YYYY")}.pdf`;

          downloadPDF(documentFileName, data.document);
          onDismiss();
        },
        onError: (error) => {
          const errorMessage =
            apiV2AssertError(error)?.response?.data.message ??
            "An unknown error occurred";
          setErrorMessage(errorMessage);
        },
      },
    );
  };

  const handleSendInvoice = ({
    invoice,
    emails,
  }: {
    emails: string[];
    invoice: typeof sellarCreditInvoice;
  }) => {
    setErrorMessage("");
    setErrorMessage("");
    if (!invoice) {
      setErrorMessage("Cannot find invoice associated with this order");
      return;
    }
    orderDocumentSendMutation.mutate(
      {
        id: invoice.id,
        type: "invoice",
        emails,
      },
      {
        onSuccess: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="CHECK">
                {`An invoice will be sent to ${emails.join(", ")} shortly.`}
              </Toast>
            ),
          });
          onDismiss();
        },
        onError: (error) => {
          const errorMessage =
            apiV2AssertError(error)?.response?.data.message ??
            "An unknown error occurred";
          setErrorMessage(errorMessage);
        },
      },
    );
  };

  const loading =
    orderDocumentDownloadMutation.isPending ||
    orderDocumentSendMutation.isPending;
  return (
    <CustomOrderCreateInvoiceDialog
      onDownload={() =>
        handleDownloadInvoice({
          invoice: sellarCreditInvoice,
        })
      }
      onSendToBusiness={() => {
        handleSendInvoice({
          emails: buyerEmails,
          invoice: sellarCreditInvoice,
        });
      }}
      onSendToUser={() => {
        handleSendInvoice({
          emails: [user?.email].filter(Boolean),
          invoice: sellarCreditInvoice,
        });
      }}
      loading={loading}
      open={open}
      onDismiss={onDismiss}
      error={errorMessage}
      options={[
        {
          value: "sendToBusiness",
          title: isBuyer ? "Send to accounts" : "Send to them",
          subtitle: `A PDF will be sent to: ${buyerEmails.join(", ")}`,
        },
        {
          value: "sendToUser",
          title: "Send to me",
          subtitle: `A PDF will be sent to: ${user?.email}`,
        },
        {
          value: "download",
          title: "Download",
          subtitle: "A PDF will be downloaded directly to your device.",
        },
      ]}
    />
  );
};

type InvoiceAction = "download" | "sendToUser" | "sendToBusiness";
type InvoiceOption = {
  value: InvoiceAction;
  title: string;
  subtitle: string;
};

type CustomOrderCreateInvoiceDialogProps = {
  loading: boolean;
  open: boolean;
  error: string;
  options: InvoiceOption[];
  onDownload: () => void;
  onSendToUser: () => void;
  onSendToBusiness: () => void;
  onDismiss: () => void;
};

const CustomOrderCreateInvoiceDialog = ({
  open,
  error,
  loading,
  options,
  onDismiss,
  onDownload,
  onSendToBusiness,
  onSendToUser,
}: CustomOrderCreateInvoiceDialogProps) => {
  const [selectedOption, setSelectedOption] =
    useState<InvoiceAction>("sendToBusiness");
  return (
    <Dialog active={open} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="VAT invoice"
      />
      <DialogContent>
        {error && <Alert type="error" message={error} />}
        {loading && <SpinnerLegacy />}
        {!loading && (
          <RadioSelector
            onChange={(event) => {
              setSelectedOption(event.currentTarget.value as InvoiceAction);
            }}
            value={selectedOption}
            required={true}
          >
            {options.map((option) => (
              <RadioSelectorOption key={option.value} value={option.value}>
                <H5>{option.title}</H5>
                {option.subtitle}
              </RadioSelectorOption>
            ))}
          </RadioSelector>
        )}
        {loading && selectedOption === "download" && (
          <>
            Download can take up to 10 seconds. Please do not navigate away
            until your download is complete.
          </>
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={() => {
            if (selectedOption === "download") {
              onDownload();
            }
            if (selectedOption === "sendToBusiness") {
              onSendToBusiness();
            }
            if (selectedOption === "sendToUser") {
              onSendToUser();
            }
          }}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
